import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { BOOT_LOCALE, LL } from 'i18n/i18n.service';
import { offeredTrialPeriod } from './checkout-trial';
import { AuthService } from 'services/auth.service';
import { getSupportEmail } from 'environments/environment-helpers';
import { DateTime } from 'luxon';
import { AuthRole } from '@cf-shared/couch-auth';
import { sleep, timeout, TimeoutError } from '@cf-shared/shared-util';
import { getMyApp } from 'app/app-interface';
import { CsPage, NavController } from 'providers/nav-controller';
import { LoggingService } from 'services/logging.service';
import { SettingsService } from 'services/settings.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TelemetryService } from 'services/telemetry.service';
import { PayProPriceFetcher, PlanOption } from '../../services/paypro-price-fetcher.service';
import { SubscriptionStatus, isPausedPaddleSubscriber } from '@cf-shared/data-model/couch-user';
import { shouldBlockCrossPlatformPurchase, licenseTypeToBillingProvider } from '@cf-shared/data-model/subscription-channel-rules';
import { ReferralClaimRefusal, normalizeTypedReferralCode } from '@cf-shared/data-model/referral';
import { ReferralService, REFERRAL_REFUSAL_MESSAGES } from 'services/referral.service';
import { TranslatedString } from 'models/translated-string';
import { ILoginPage } from 'pages/login/login-interface';
import { ISettingsPage } from 'pages/settings-page/settings-page-interface';
import { IRedeemCodePage } from 'pages/redeem-code/redeem-code-interface';
import { MenuController } from 'providers/menu-controller';
import { ExternalFlowService } from 'services/external-flow.service';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { isCapacitorNative, getCapacitorPlatform } from 'environments/environment-helpers';
import { getSharedUrlOrigin } from 'util/backend-origin';
import { IAPService, IAPProduct } from 'services/iap.service';
import { Capacitor } from '@capacitor/core';
import { openExternalLink, APPLE_EULA_URL } from 'util/open-external-link';
import { PaddleCheckoutService } from 'services/paddle-checkout.service';
import { OnlineStateService } from 'services/online-state.service';
import { isTransientFetchError } from 'services/error-patterns';
import { claimsOffline, classifyPricingFailure, mustReport, offersInPlaceRetry } from './pricing-failure-policy';
// aliased: the page exposes a getter of the same name to the template, and a
// bare call would read as if the getter were calling itself.
import { canOfferPurchase as evaluateCanOfferPurchase } from './purchase-offer-policy';
import { shouldShowCoachLapseNotice } from './coach-lapse-notice-policy';
import { CoachRelationship, coachRelAsClientId } from '@cf-shared/data-model/coach';
import { isPouchNotFound } from '@cf-shared/data-model/pouch';
import { UserDb } from 'services/user-db.service';
import { ImageUploadOutboxService } from 'services/image-upload-outbox.service';
import { UrlQueryService } from 'services/url-query-service';
import { describeError } from '@cf-shared/error-describe';
import { SubscribePaddleRail } from './subscribe-paddle-rail';
import { SubscribePayProRail } from './subscribe-paypro-rail';
import { SubscribeNativeRail } from './subscribe-native-rail';
import { CheckoutEmailVerdict } from './checkout-email-verdict';
import { adConsentChangedEvent, PubSubRegistration, PubSubService } from 'services/pubsub.service';
import { resolveEffectiveAdConsent } from 'services/ad-consent-cookie';
import { AdConsentAnswer } from '@cf-shared/ad-consent';
import { AdConsentRefreshDecision, decideAdConsentRefresh } from './ad-consent-refresh-policy';
import { resendSignInLinkAsync } from './resend-link-outcome';
import { LinkResendOutcome } from 'services/deep-link-signin';
import { Subscription } from 'rxjs';

@Component({
  selector: 'page-subscribe',
  templateUrl: 'subscribe.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubscribePage extends CsPage<{}> {
  protected LL = LL;
  expired = false;
  unlimitedLicense = false;
  selectedPlan = undefined;

  // the Advertising answer the current checkout was built with (#5587), and the
  // subscription that tells this page the answer changed. See
  // refreshCheckoutForAdConsentAsync.
  private checkoutAdConsent: AdConsentAnswer | undefined;
  private adConsentRegistration: PubSubRegistration | undefined;
  // listens for the server login that loads this person's subscription record,
  // so the page can decide again once it arrives. See onServerSubscriptionRecord.
  private subscriptionRecordListener: Subscription | undefined;
  // true while this page's record-based decisions were made with no record. Only
  // then does a record arriving later change anything. A sign-in the page starts
  // itself (the purchase-first checkout) must NOT trigger a re-decision: the
  // checkout is finishing that sign-in and reloads the app on its own
  private decidedWithoutSubscriptionRecord = false;
  // ngOnInit holds the price load back while the record is unknown, because
  // opening a checkout into a frame the page keeps hidden fails and alerts; the
  // record's arrival starts it, unless the person turns out to be paused
  private priceLoadWaitsForRecord = false;
  // set once the record shows a subscription the person can cancel or resume, while
  // the page is leaving for their default page. Only then does canOfferPurchase veto
  // a subscriber: a buyer whose new subscription lands while they are still on the
  // Paddle success screen must keep its Continue button
  private leavingForSubscriber = false;
  country: string = undefined; // this is set when querying prices
  previousCheckoutError: string = undefined;
  previousCheckoutWaiting: boolean = false;
  previousCheckoutWaitingTime: string = undefined;
  exitingPage = false;

  loggedIn = false; // used so we can hide the header and menu if the user is not logged in
  // the signed-in account's address, shown so a user on the wrong account can tell (#5894)
  signedInEmail: string = undefined;

  planOptions: PlanOption[] = [];

  // True when the app runs inside Capacitor on a phone rather than in a browser.
  // The branch selector for the whole page — the native twin of `webProvider` —
  // which is why it stays here rather than moving into the native rail with the
  // rest of the store state.
  isNativePlatform = false;
  crossPlatformSubscription = false;
  // True when this user's Paddle membership is PAUSED. They have no access, so
  // they land here from every paywall — but the right action is Resume, not a
  // second purchase. Hides the plan picker and shows the resume guidance.
  pausedMembership = false;

  // #4393 Surface A. True when this user's Premium came from a COACH whose own
  // plan has now ended, AND that coaching relationship is still live. The
  // template then shows an explanation instead of the generic "your
  // subscription is inactive" line, which means nothing to somebody who never
  // bought a subscription. Resolved in ionViewWillEnter — deliberately NOT in
  // ionViewCanEnter; see resolveCoachLapseStateAsync for why.
  coachLapsed = false;
  // The coach's display name, when the local as-client document carried one.
  // Empty selects the no-name wording; it is never a reason to hide the card.
  coachLapsedCoachName = '';

  /**
   * Whether it is safe to show ANY purchase surface — the plan picker, either
   * web checkout card, the native IAP card, or the "already have a
   * subscription" card.
   *
   * One getter rather than the condition spelled out at each site, so a newly
   * added surface cannot get it wrong. The rule itself lives in
   * `purchase-offer-policy.ts` so it can be unit-tested without a page harness;
   * that file also records the double-charge bug it exists to prevent. (#4189)
   */
  get canOfferPurchase(): boolean {
    return evaluateCanOfferPurchase({
      pausedMembership: this.pausedMembership,
      crossPlatformSubscription: this.crossPlatformSubscription,
      previousCheckoutWaiting: this.previousCheckoutWaiting,
      loadingPrices: this.loadingPrices,
      subscriptionRecordUnknown: this.waitingForSubscriptionRecord,
      alreadySubscribed: this.leavingForSubscriber,
    });
  }

  /**
   * True while a signed-in person's subscription record has not come back from
   * the server yet. A warm restart shows this page before its server login, and
   * every "do they already pay?" check here reads that record, so until it
   * arrives the page sells nothing and shows its spinner (df-6cbfc9ac5a80).
   * A signed-out buyer has no record to wait for: the purchase-first flow sells
   * to them and signs them in afterwards.
   */
  get waitingForSubscriptionRecord(): boolean {
    return !!this.auth.user && !this.auth.couch;
  }

  // true when the cross-platform membership is the web (PayPro) rail, so
  // the message points to the web instead of "the device you subscribed on"
  crossPlatformIsWeb = false;

  /* All these booleans are a bit messy, but need to have a complex logic for when is which of the elements visible when:

  - Prices will load before checkout, but don't want to show them until both are loaded.
  - Selecting a different price will load the checkout again, but we don't want to hide the prices during that.
  */
  loadingPrices = false;
  loadingCheckout = false;

  // Paddle inline-checkout state (#3861). `webProvider` is 'paddle' when the
  // server picked Paddle for this user; the template then renders the Paddle
  // inline container (`.paddle-checkout-frame`) instead of the PPG iframe.
  webProvider: 'payPro' | 'paddle' = 'payPro';

  /**
   * #4962: the server built this checkout with the referral prices, so the friend
   * really is getting their free month. Rendered as one line on the WEB rail only —
   * the native store screens must say nothing about a free month, because the app
   * cannot deliver one there and a promise it cannot keep is worse than silence.
   */
  referralFreeMonthApplied = false;

  /**
   * #6145: the free trial this web checkout really includes, written out ("14 days",
   * "1 month"), or undefined when the screen must not promise one. Undefined keeps the
   * ordinary "Select Subscription" wording: on the native screens (which show their own
   * per-plan trial line), on the PayPro rail (which cannot say who gets the trial), and
   * whenever the Paddle prices carry no trial or could not be read.
   */
  get webTrialText(): string | undefined {
    if (this.isNativePlatform || this.webProvider !== 'paddle') return undefined;
    // two independent sources must agree before the screen promises a trial: the server
    // saying it handed out trial prices (#6552), and the prices themselves carrying one
    if (this.payProPriceFetcher.paddleConfig?.trialIncluded !== true) return undefined;
    const period = offeredTrialPeriod(this.planOptions.map(p => p.trialPeriod), this.referralFreeMonthApplied);
    if (!period) return undefined;
    const count = period.frequency;
    switch (period.interval) {
      case 'day': return LL.subscribe.trialPeriod.days({ count });
      case 'week': return LL.subscribe.trialPeriod.weeks({ count });
      case 'month': return LL.subscribe.trialPeriod.months({ count });
      case 'year': return LL.subscribe.trialPeriod.years({ count });
    }
  }

  /** #4962: show the "were you invited?" code box. Server-decided; web rail only. */
  offerReferralCodeEntry = false;

  /** What the person has typed into that box. */
  referralCodeInput = '';

  /** A refusal to show under the box, or undefined when there is nothing to say. */
  referralCodeError?: TranslatedString;

  /** True while a claim is in flight, so the button can be disabled. */
  referralCodeSubmitting = false;

  /**
   * The Paddle inline-checkout rail, with all of its own state.
   *
   * Built in the constructor and dropped together with this page, because
   * everything it holds belongs to ONE visit here.
   */
  private paddleRail: SubscribePaddleRail;

  /**
   * The PayPro checkout-iframe rail, with all of its own state. Built and dropped
   * with this page for the same reason the Paddle rail is — see
   * `subscribe-paypro-rail.ts`.
   */
  private payProRail: SubscribePayProRail;

  /**
   * The native Apple App Store / Google Play rail, with all of its own state.
   * Built and dropped with this page like the two web rails, and for the same
   * reason — see `subscribe-native-rail.ts`, which also records that nothing in
   * this repository can test that rail.
   */
  private nativeRail: SubscribeNativeRail;

  /** The plans the native store offered, for the template's native plan list. */
  get iapProducts(): IAPProduct[] {
    return this.nativeRail.iapProducts;
  }

  /** The native plan the Subscribe button would buy, for that button's label. */
  get selectedIAPProduct(): IAPProduct {
    return this.nativeRail.selectedIAPProduct;
  }

  /** True while a store purchase or restore is running, so the buttons disable. */
  get purchaseInProgress(): boolean {
    return this.nativeRail.purchaseInProgress;
  }

  /** True on iPhone/iPad specifically, which gates the Apple-only surfaces. */
  get isIOSNative(): boolean {
    return this.nativeRail.isIOSNative;
  }

  /**
   * True once Paddle reported the payment succeeded. The template then shows
   * Paddle's own success screen plus our Continue button, instead of reloading
   * out from under that success screen.
   */
  get paddlePurchaseComplete(): boolean {
    return this.paddleRail.purchaseComplete;
  }

  /** The PayPro buy URL the template feeds to the checkout iframe's src. */
  get currentBuyUrl(): SafeResourceUrl {
    return this.payProRail.currentBuyUrl;
  }

  /** How tall the template renders that iframe, as the frame itself reported it. */
  get checkoutFrameHeight(): number {
    return this.payProRail.checkoutFrameHeight;
  }

  supportEmail = getSupportEmail();
  supportMailUrl = "mailto:" + getSupportEmail();

  tosUrl = getSharedUrlOrigin() + '/tos.html';
  privacyUrl = getSharedUrlOrigin() + '/privacy.html';
  refundUrl = getSharedUrlOrigin() + '/refund.html';
  eulaUrl = APPLE_EULA_URL;

  // on capacitor native, intercept legal-link taps and route through
  // browser.open (in-app safari overlay) — see open-external-link.ts
  // for why. on web, do nothing: the native <a target="_blank">
  // handles middle-click / right-click affordances.
  handleLegalLink(ev: MouseEvent, url: string) {
    if (!isCapacitorNative()) return;
    ev.preventDefault();
    openExternalLink(url).catch(err => this.logging.trackAbnormalException(err));
  }

  constructor(
    private navCtrl: NavController,
    private menuCtrl: MenuController,
    private auth: AuthService,
    public logging: LoggingService,
    public settings: SettingsService,
    protected sanitizer: DomSanitizer,
    private telemetry: TelemetryService,
    private payProPriceFetcher: PayProPriceFetcher,
    private referralService: ReferralService,
    private externalFlow: ExternalFlowService,
    private alertCtrl: AlertController,
    private cloudFunctions: CloudFunctionsService,
    // injected for one rail each — the page itself never talks to a store, to
    // Paddle, or to the checkout tracking data, so these are plain parameters
    // rather than fields on this class. `imageUploadOutbox` is here for the
    // sign-out guard the native rail's already-linked alert runs.
    iapService: IAPService,
    imageUploadOutbox: ImageUploadOutboxService,
    paddleCheckout: PaddleCheckoutService,
    private onlineState: OnlineStateService,
    private userDb: UserDb,
    urlQuery: UrlQueryService,
    private pubSub: PubSubService,
    private cdr: ChangeDetectorRef
  ) {
    super();
    this.isNativePlatform = isCapacitorNative();
    console.log(`Subscribe: platform=${Capacitor.getPlatform()} isNative=${Capacitor.isNativePlatform()} androidBridge=${!!(window as any).androidBridge}`);
    // all three rails borrow the page state they must not own. Arrow functions
    // rather than bound methods, so `this` is this page however a rail calls
    // them — and so `viewDestroyed` and `safeTimeout`, which are protected on
    // CsPage, stay reachable from inside the class that owns them.
    this.payProRail = new SubscribePayProRail(
      { auth, logging, alertCtrl, navCtrl, sanitizer },
      {
        markPageForCheck: () => this.cdr.markForCheck(),
        scheduleOnPage: (callback, delayMs) => { this.safeTimeout(callback, delayMs); },
        setLoadingCheckout: (loading) => { this.loadingCheckout = loading; },
        isLoadingCheckout: () => this.loadingCheckout,
        classifyCheckoutEmailAsync: (email) => this.classifyCheckoutEmail(email),
      }
    );
    this.paddleRail = new SubscribePaddleRail(
      { auth, logging, settings, alertCtrl, navCtrl, cloudFunctions, paddleCheckout, urlQuery },
      {
        isViewGone: () => this.viewDestroyed,
        markPageForCheck: () => this.cdr.markForCheck(),
        scheduleOnPage: (callback, delayMs) => { this.safeTimeout(callback, delayMs); },
        setLoadingCheckout: (loading) => { this.loadingCheckout = loading; },
        setPlanOptions: (options) => { this.planOptions = options; },
        classifyCheckoutEmailAsync: (email) => this.classifyCheckoutEmail(email),
      }
    );
    this.nativeRail = new SubscribeNativeRail(
      { auth, logging, alertCtrl, iapService, onlineState: this.onlineState, userDb: this.userDb, imageUploadOutbox },
      {
        // fixed for the life of the app run, so a value rather than a method.
        isNativePlatform: this.isNativePlatform,
        isViewGone: () => this.viewDestroyed,
        markPageForCheck: () => this.cdr.markForCheck(),
        setLoadingPrices: (loading) => { this.loadingPrices = loading; },
        isLoadingPrices: () => this.loadingPrices,
        // read through a function, NOT captured here: both flags are resolved in
        // ionViewCanEnter, which runs after this constructor. A value captured
        // now would read false for the rest of the visit, and the rail would show
        // an already-covered user the "could not load options" dead end.
        hasCrossPlatformSubscription: () => this.crossPlatformSubscription,
        hasUnlimitedLicense: () => this.unlimitedLicense,
        setSelectedPlan: (index) => { this.selectedPlan = index; },
      }
    );
  }

  shouldShowSettingsButton(): boolean {
    // show settings button when menu is not visible and user is logged in
    // menu is visible when shouldShowMenu returns a truthy value ('md' or true)
    return !getMyApp().shouldShowMenu() && this.loggedIn;
  }

  // #3969: true when the native store finished loading but returned no
  // purchasable products AND the user actually needs to buy here. The rail owns
  // the rule and the matching Sentry report; see SubscribeNativeRail.
  shouldShowNoStoreProductsMessage(): boolean {
    return this.nativeRail.shouldShowNoStoreProductsMessage();
  }

  onSettingsClick() {
    void this.navCtrl.setRootAsync(ISettingsPage, {});
  }

  async ionViewCanEnter() {
    this.leavingForSubscriber = false;
    if (this.isSubscriberWhoMustLeave()) {
      // must not wait, and must not start yet: redirect only after this refused navigation
      // has put the previous page list back, or the two race over the page stack
      // (df-2077c7f7295e, design-docs/navigation.md, "A refused navigation and a navigation
      // its guard starts"). A plain timer on purpose: cancelling the navigation destroys this
      // page, and that cancels every safeTimeout it owns.
      setTimeout(() => void getMyApp().gotoDefaultPageAsync(), 0);
      return false;
    }

    const { role, expiry_utc, subscriptionExpired } = this.auth.getAuthRoleAndExpiry();
    this.expired = subscriptionExpired;

    this.applyServerSubscriptionRecord();

    this.unlimitedLicense = expiry_utc === 'never' && role > AuthRole.Paid;

    this.previousCheckoutError = this.externalFlow.getLastCheckoutError();

    this.cdr.markForCheck();

    return true;
  }

  // whether this person already has a subscription they can cancel or resume, so
  // the page has nothing to sell them. Both answers come from the server's
  // subscription record, so before it is loaded this is false
  private isSubscriberWhoMustLeave(): boolean {
    return this.auth.getAuthRole() >= AuthRole.Paid
      && (this.auth.getCanResumeSubscription() || this.auth.getCanCancelSubscription());
  }

  // the part of the entry decision that reads the server's subscription record.
  // ionViewCanEnter runs it, and onServerSubscriptionRecord runs it again when the
  // record arrives after the page opened: a warm restart shows this page before its
  // server login, so on entry every value here is worked out from no record at all
  // (df-6cbfc9ac5a80). One method, so the two cannot drift apart.
  private applyServerSubscriptionRecord() {
    this.decidedWithoutSubscriptionRecord = this.waitingForSubscriptionRecord;
    const role = this.auth.getAuthRole();

    // if the membership lives on a channel that can't be managed/purchased
    // from here, show the cross-platform note instead of the buy options.
    // A store membership is manageable only in its own store app; the web
    // membership (either web rail — PayPro or Paddle) only on the web. This
    // prevents stacking a second store subscription on top of a web
    // membership. (The profile page shows a parallel `subscribedOffPlatform`
    // note for active subs; this purchase gate is separate — it is reached by
    // free/lapsed users who must be able to buy, so it needs the
    // active-membership gate below.) Shares the one rule helper
    // (`shouldBlockCrossPlatformPurchase`) so the two surfaces can't drift.
    //
    // Gate on `role >= AuthRole.Paid` so we only steer to the other channel
    // while that membership is STILL ACTIVE. Once it has expired the user is
    // effectively free and must be able to subscribe via this platform's
    // native store — a former web member (PayPro or Paddle) whose sub lapsed
    // keeps licenseType set to that web rail forever (the dead block is never
    // deleted), so without this guard the Subscribe page would wrongly block
    // them from ever buying via the App Store / Play Store (#3826).
    // pass coachTierData.provider so an active coach (licenseType CoachTier)
    // resolves to their store and a cross-provider personal purchase is
    // blocked (V6 reverse-direction mapping — coach-tier-store-coordination).
    const provider = licenseTypeToBillingProvider(this.auth.couch?.licenseType, this.auth.couch?.coachTierData?.provider);
    // A PAUSED Paddle sub grants no access (the role drops to Unknown) but is
    // still live and resumable — treat it as a membership so the user cannot buy
    // a second subscription and end up double-billed (#4189).
    //
    // The shared helper already requires `licenseType === Paddle`, so a residual
    // paddleData block left by a provider migration cannot make an Apple/Google
    // subscriber look paused here (this is the purchase gate). That also means a
    // separate `provider === 'paddle'` term would be redundant — `provider` is
    // derived from the same licenseType.
    const isPausedPaddleMembership = isPausedPaddleSubscriber(this.auth.couch);
    // On WEB the cross-provider check above does NOT fire for a paused Paddle
    // sub — the membership IS manageable on web, so nothing blocks it. But a
    // paused user has no access, so every paywall in the app sends them here,
    // where the obvious action is "buy" rather than "resume". Buying would
    // charge them a second time AND orphan the paused subscription, which stays
    // alive and resumable at Paddle. So surface the paused state explicitly and
    // point them at Resume. (#4189)
    this.pausedMembership = isPausedPaddleMembership;
    this.crossPlatformSubscription = shouldBlockCrossPlatformPurchase(provider, getCapacitorPlatform(), role >= AuthRole.Paid, isPausedPaddleMembership);
    // both web rails count as "web" here, so a Paddle (or PayPro) sub
    // seen inside a store app gets the "manage on the web" copy.
    this.crossPlatformIsWeb = this.crossPlatformSubscription && (provider === 'payPro' || provider === 'paddle');

    // a checkout that is still being confirmed. start the countdown only when this
    // turns on, so a second run of this method does not start a second one
    const wasWaiting = this.previousCheckoutWaiting;
    this.previousCheckoutWaiting = this.auth.getSubscriptionStatus() === SubscriptionStatus.Waiting;
    if (this.previousCheckoutWaiting && !wasWaiting) {
      void this.waitingTimer();
    }
  }

  // the server login loaded (or reloaded) this person's subscription record while
  // the page is open. if they turn out to have a subscription, take them to their
  // default page, as ionViewCanEnter would have; otherwise redo the record-based
  // checks so the paused and other-platform states show instead of buy options
  private onServerSubscriptionRecord() {
    if (this.viewDestroyed || this.exitingPage) return;
    if (this.isSubscriberWhoMustLeave()) {
      // the record is in, so this is the one re-decision; a second login must not navigate again
      this.decidedWithoutSubscriptionRecord = false;
      // nothing is sold while the page leaves
      this.leavingForSubscriber = true;
      this.cdr.markForCheck();
      // ask rather than navigate: a login retry or warm resume Phase B may be moving the
      // person already, and a second navigation would run alongside theirs. the request
      // waits for them and is dropped if this page is no longer on screen by then; if it is
      // dropped, decide again when the page is shown again, since the page may only have been
      // covered (df-53c89a39b0e6, design-docs/warm-resume.md)
      console.log('Subscribe: the server login shows an active subscription; asking to leave the subscribe page');
      this.navCtrl.requestPostLoginRoute(
        'the subscribe page, for a subscriber',
        () => getMyApp().gotoDefaultPageAsync(),
        () => !this.viewDestroyed && !this.exitingPage,
        () => { this.decidedWithoutSubscriptionRecord = true; },
      );
      return;
    }
    this.applyServerSubscriptionRecord();
    if (this.priceLoadWaitsForRecord) {
      this.priceLoadWaitsForRecord = false;
      this.startPriceLoadUnlessPaused();
    }
    this.cdr.markForCheck();
  }

  // this loops counting the seconds until the waiting time is over, and then reloads the page
  async waitingTimer() {
    let waitingUntil = DateTime.fromISO(this.auth.couch.licenseExpires_utc); // DateTime.local().plus({ minutes: 15 }).toUTC();
    for (; ;) {
      let diff = waitingUntil.diff(DateTime.local().toUTC(), 'seconds').seconds;
      if (diff <= 0) {
        break;
      }
      diff = Math.floor(diff);
      let seconds = diff % 60;
      let minutes = Math.floor(diff / 60);
      this.previousCheckoutWaitingTime = `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
      await sleep(100); // sleep less than a second, so the display is more precise
    }
    await this.onReloadAfterWaitingClicked();
  }

  async onReloadAfterWaitingClicked() {
    await getMyApp().reloadAppAsync({ resetToRoot: true });
  }

  async ionViewDidEnter() {
    console.log('Subscribe: Enter page.');
    this.exitingPage = false;

    // PayPro-specific setup (web only). The rail stores the return-host cookie
    // and attaches its iframe-message listener — see SubscribePayProRail.enterPage.
    if (!this.isNativePlatform) {
      this.payProRail.enterPage();
    }

    // an Advertising answer given while this page is open — most often the cookie
    // dialog a first-time visitor answers on top of it — must reach the checkout
    this.adConsentRegistration?.unsubscribe();
    this.adConsentRegistration = this.pubSub.subscribe(adConsentChangedEvent, () => {
      void this.refreshCheckoutForAdConsentAsync();
    });

    // the server's subscription record may arrive after this page opened (a warm
    // restart logs in to the server only after showing the page). listen only if
    // the entry decision was made without it. the source replays the current
    // record on subscribe, so a login that finished between ionViewCanEnter and
    // now is seen too. the first record ends the wait: applying it clears
    // decidedWithoutSubscriptionRecord, so later logins are ignored
    this.subscriptionRecordListener?.unsubscribe();
    this.subscriptionRecordListener = undefined;
    if (this.decidedWithoutSubscriptionRecord) {
      this.subscriptionRecordListener = this.auth.couchAuthChanged.subscribe(couchAuth => {
        if (couchAuth && this.decidedWithoutSubscriptionRecord) this.onServerSubscriptionRecord();
      });
    }

    this.cdr.markForCheck();
  }

  async ionViewWillEnter() {
    // menu is disabled if the user is not logged in
    this.loggedIn = !!this.auth.user;
    this.signedInEmail = this.auth.user?.email || undefined;
    this.menuCtrl.enable(this.loggedIn);

    await this.resolveCoachLapseStateAsync();
    // this method used to be fully synchronous; the awaited read above means
    // the view can be torn down before we get here, and markForCheck on a
    // destroyed view is the NG0911 case.
    if (this.viewDestroyed) return;

    this.cdr.markForCheck();
  }

  /**
   * Decide whether this user gets the coach-specific paywall message (#4393).
   *
   * ## Why this runs here and not in ionViewCanEnter
   *
   * `ionViewCanEnter` is the NAVIGATION GATE for the only page a revoked user
   * can reach — this page redirects by returning `false` from it. An async read
   * placed there that throws or hangs would leave the client with no
   * explanation AND no way to subscribe, which is strictly worse than showing
   * them nothing new. So the read happens after entry is already settled, and
   * the page renders either way.
   *
   * ## Why a failed read hides the card
   *
   * Liveness (`coachUid`) and the coach's name both come from the one local
   * `coach-relationship/as-client` document, so a read that failed cannot
   * confirm liveness. Without that confirmation the card must not render: a
   * DETACHED client's `coachData` block is also present and also non-granting
   * (coachDetachClient leaves it behind, set to Terminated), and this card would
   * tell them they are still connected to a coach they no longer have.
   */
  private async resolveCoachLapseStateAsync(): Promise<void> {
    // start from the safe answer, so every early return below lands on the
    // generic warning rather than on a stale value from a previous entry.
    this.coachLapsed = false;
    this.coachLapsedCoachName = '';

    const coachData = this.auth.couch?.coachData;
    // offline (auth.couch is null) or never coached — nothing to explain.
    if (!coachData) return;

    let relationshipConfirmedLive = false;
    let coachName = '';
    try {
      // a one-off read through the throwaway-watcher helper (the page does not
      // need to react to later changes — a revoked user is not receiving sync).
      const relDoc = await this.userDb.database.withDocWatcherAsync(
        watcher => this.userDb.database.getTypedRecordAsync<CoachRelationship>(coachRelAsClientId, watcher));
      relationshipConfirmedLive = !!relDoc?.coachUid;
      coachName = relDoc?.coachName || '';
    } catch (err) {
      // expected: a not-found rejection from getTypedRecordAsync when the
      // as-client document has not replicated to this device. Liveness is then
      // simply unconfirmed, which the early return below already handles.
      if (!isPouchNotFound(err)) {
        // describeError, not String(err): a PouchDB read rejects with a
        // { status, error, reason } object, which String() flattens to
        // "[object Object]" — and this value crosses the wire as a telemetry
        // property, so it must never carry a stack either.
        this.logging.trackSilentProblem('SubscribeCoachRelationshipReadFailed', {
          error: describeError(err),
        });
      }
      return;
    }

    if (this.viewDestroyed) return;

    // read the role fresh rather than reusing the page's `expired` flag — see
    // `hasPremiumAccess` in the policy for why that flag is false for exactly
    // the users this card is for.
    const { role } = this.auth.getAuthRoleAndExpiry();

    this.coachLapsed = shouldShowCoachLapseNotice({
      hasPremiumAccess: role >= AuthRole.Paid,
      coachData,
      relationshipConfirmedLive,
      now: DateTime.utc(),
    });
    this.coachLapsedCoachName = this.coachLapsed ? coachName : '';
  }

  async ionViewWillLeave() {
    this.exitingPage = true;
    console.log('PayPro: Leaving page');
    this.menuCtrl.enable(true);

    // detach the native rail's storefront-change listener so re-fetches don't
    // fire against a destroyed view — see SubscribeNativeRail.teardown. Called
    // unconditionally, like the PayPro rail's: on web the rail never registered
    // one and this does nothing.
    this.nativeRail.teardown();

    this.adConsentRegistration?.unsubscribe();
    this.adConsentRegistration = undefined;
    this.subscriptionRecordListener?.unsubscribe();
    this.subscriptionRecordListener = undefined;

    // detach the PayPro iframe-message listener attached in ionViewDidEnter —
    // see SubscribePayProRail.teardown for what leaving it attached would cost.
    this.payProRail.teardown();

    // tear down any open Paddle inline checkout so it doesn't linger across
    // navigation (the eventCallback closure captures this now-stale page). The
    // rail also resets its own post-purchase flags — see SubscribePaddleRail.teardown.
    if (this.webProvider === 'paddle') {
      this.paddleRail.teardown();
    }

    this.cdr.markForCheck();
  }

  ngOnInit() {
    // the paused check below, and every other "do they already pay?" check, needs
    // the server's record. Without it, wait: onServerSubscriptionRecord starts the
    // load once the record arrives (df-6cbfc9ac5a80)
    if (this.decidedWithoutSubscriptionRecord) {
      this.priceLoadWaitsForRecord = true;
      return;
    }
    this.startPriceLoadUnlessPaused();
  }

  private startPriceLoadUnlessPaused() {
    // A paused member is not buying anything here — every purchase surface is
    // hidden and the page tells them to resume instead. Skip the whole price
    // load: running it would set loadingCheckout, then fail to find the (hidden)
    // checkout container, leaving a permanent spinner overlay across the paused
    // message — and it would also emit a begin_checkout event and a
    // PaddleFrameTargetMissing Sentry report for a state that is working as
    // designed. pausedMembership is resolved by applyServerSubscriptionRecord,
    // which runs before both callers of this (ionViewCanEnter, awaited before
    // ngOnInit, and onServerSubscriptionRecord), so it is already correct here. (#4189)
    if (this.pausedMembership) {
      this.loadingPrices = false;
      this.cdr.markForCheck();
      return;
    }
    void this.loadPricesAsync();
  }

  /** Fetch the subscription prices and pre-select a plan, retrying while the user asks us to.
   *
   * F7 (#3991): a failure here used to have exactly one recovery — reloading the whole app.
   * Offline, that is a bad answer: the reload cannot succeed either, so the user is bounced
   * through a full app restart only to land back on a page that still cannot load prices. So
   * a failure that is (or looks like) a connectivity failure now gets an offline alert with a
   * Try Again that just runs the fetch again in place — no app reload. Every other failure
   * keeps the old behavior exactly: report to Sentry, then the reload alert.
   *
   * This is a loop rather than a recursive call because the user can tap Try Again as many
   * times as they like, and recursing would grow the stack on every attempt. */
  private async loadPricesAsync(): Promise<void> {
    while (!this.viewDestroyed && !this.exitingPage) {
      const userWantsAnotherTry = await this.attemptPriceLoadAsync();
      if (!userWantsAnotherTry) return;
    }
  }

  /** One attempt at loading the prices. Returns true only when the user tapped Try Again on
   *  the offline alert and we should attempt it again. */
  private async attemptPriceLoadAsync(): Promise<boolean> {
    try {
      if (this.isNativePlatform) {
        await timeout(30000, this.nativeRail.fetchPricesAsync());
      } else {
        await timeout(30000, this.fetchPrices());
      }
      if (this.exitingPage) {
        return false;
      }
      if (!this.isNativePlatform) {
        this.handleSelect(0);
      }
      return false;
    } catch (err) {
      // The error's own shape decides whether a retry is worth offering; our connectivity belief
      // decides what we CLAIM. Keeping those apart is the whole point of the policy module — a
      // transient fetch error is not proof of being offline (an ad blocker or proxy produces the
      // same TypeError on a perfectly good connection), and being offline is not proof that the
      // error was about connectivity. See pricing-failure-policy.ts.
      const kind = classifyPricingFailure({
        isTransientFetchError: isTransientFetchError(err),
        isTimeout: err instanceof TimeoutError,
        isOffline: !this.onlineState.isLikelyOnline(),
      });

      if (offersInPlaceRetry(kind)) {
        console.log(`Subscribe: pricing fetch failed (${kind})`, err);
        if (mustReport(kind)) {
          // everything except a plain "network error while we know we are offline" is something we
          // cannot fully explain — an unexplained throw, or a network-shaped failure on a
          // connection we believe is working (blocked by an extension, a proxy, or CORS). The user
          // still just gets a Try Again, but the error must not vanish.
          this.logging.trackSilentProblem(`Subscribe: pricing load failed (${kind})`, {
            error: err?.message ?? String(err),
            name: err?.name,
          });
        }
        return await this.showPricingRetryAlertAsync(claimsOffline(kind));
      }

      // a real, non-connectivity failure — unchanged from before
      this.logging.trackAbnormalException(err);
      await this.alertCtrl.showAlertAsync({
        title: LL.subscribe.alerts.pricingFailed.title(),
        message: LL.subscribe.alerts.pricingFailed.message(),
        buttons: [{
          text: LL.fatalError.buttons.retry(),
          role: AlertButtonRole.Primary,
          result: null
        }]
      });
      if (this.viewDestroyed) return false;
      void getMyApp().reloadAppAsync({ resetToRoot: false });
      return false;
    }
  }

  /** The retryable branch of the pricing load: offer an in-page Try Again (no app reload), or
   *  let the user back out. Returns true when the user wants another attempt.
   *
   *  `claimOffline` decides the wording only. When we believe the device is offline we say so.
   *  When it isn't — the request failed but our connection looks fine — we must NOT send the user
   *  off to check their wifi, because the cause is more likely an ad blocker, a proxy, or CORS.
   *  We say the plans could not load and let them retry. */
  private async showPricingRetryAlertAsync(claimOffline: boolean): Promise<boolean> {
    if (this.viewDestroyed || this.exitingPage) return false;

    const retry = await this.alertCtrl.showAlertAsync({
      title: claimOffline ? LL.subscribe.alerts.pricingOffline.title() : LL.subscribe.alerts.pricingUnavailable.title(),
      message: claimOffline ? LL.subscribe.alerts.pricingOffline.message() : LL.subscribe.alerts.pricingUnavailable.message(),
      buttons: [
        {
          text: LL.common.buttons.cancel(),
          role: AlertButtonRole.Cancel,
          result: false,
        },
        {
          text: LL.subscribe.alerts.pricingOffline.tryAgain(),
          role: AlertButtonRole.Primary,
          result: true,
        },
      ],
    });

    // the page can go away while the alert is open (the user navigates away, or the app reloads)
    if (this.viewDestroyed || this.exitingPage) return false;

    if (retry) return true;

    // Cancel: go back — but ONLY if there is anywhere to go back to. For a locked-out or unpaid user
    // this page IS the nav root (app.component picks it as the start page), and popping the root does
    // not "go back" — it walks out of the app's own history, which is worse than doing nothing. In
    // that case stay put: the page is priceless (literally), but the header menu is still there, and
    // that beats pushing the user out of the app.
    if (this.navCtrl.canGoBack()) {
      await this.navCtrl.popAsync();
    }
    return false;
  }

  /**
   * The buyer picked a native store plan. The rail owns the selection; the page
   * keeps the index itself, because both radio groups bind it two-way.
   */
  handleNativeSelect(i: number) {
    this.nativeRail.handleNativeSelect(i);
  }

  /** The buyer tapped Subscribe on a native store plan; see SubscribeNativeRail. */
  handleNativePurchase() {
    this.nativeRail.handleNativePurchase();
  }

  /** The buyer tapped Restore Purchases (iOS only); see SubscribeNativeRail. */
  handleRestorePurchases() {
    this.nativeRail.handleRestorePurchases();
  }

  async fetchPrices() {
    this.loadingPrices = true;
    this.cdr.markForCheck();
    this.planOptions = await this.payProPriceFetcher.fetchPricesAsync();
    // the server dispatcher decided which web rail this user gets; if it picked
    // Paddle, init Paddle.js and build the plan selector from a price preview.
    this.webProvider = this.payProPriceFetcher.provider;
    // #4962: both answers come from the server with the checkout config and are
    // never recomputed here — only the server can see the referral record and
    // resolve the inviter's flag.
    this.referralFreeMonthApplied = this.payProPriceFetcher.referralFreeMonthApplied;
    this.offerReferralCodeEntry = this.payProPriceFetcher.offerReferralCodeEntry;
    if (this.webProvider === 'paddle') {
      await this.paddleRail.setupAsync(this.payProPriceFetcher.paddleConfig);
    }
    // the answer the checkout was built with, recorded AFTER the config came
    // back. It has to be after: on the Paddle rail the config carries the Global
    // Privacy Control signal the server saw (#5663), and a browser that sets only
    // the header cannot see that signal itself. Recorded before, this would say
    // "yes" for a checkout that actually carries "no" — and then a person who
    // switched Advertising on afterwards would match the stale value, the refresh
    // would be skipped, and their explicit opt-in would never reach the server.
    this.checkoutAdConsent = resolveEffectiveAdConsent(this.serverSawGpc());
    this.loadingPrices = false;
    this.cdr.markForCheck();
  }

  /**
   * Claim a referral code typed into the "were you invited?" box (#4962).
   *
   * Two things have to happen after a successful claim and NEITHER alone is enough.
   * The caller is now a referred friend, so:
   *  - the checkout must be RE-REQUESTED, because entitlement is decided server-side
   *    when the config is built and is never recomputed on the client. Without this
   *    the person has just been recorded as referred and is still looking at 14 days.
   *  - the RAIL may change underneath the page. The routing override sends an
   *    entitled friend to Paddle, so a page that was rendering PayPro has to switch
   *    branches. Miss this and a valid code is accepted, the claim is recorded, and
   *    the page still shows PayPro and the ordinary trial — the same "takes a code
   *    and delivers nothing" failure the native branch is deliberately kept away from.
   *
   * `resetForRefetch` is what makes the re-request real: the fetcher caches its
   * answer for the session, and on the PayPro branch that cache is populated, so a
   * second call would otherwise never reach the server at all.
   */
  async submitReferralCodeAsync(): Promise<void> {
    const code = normalizeTypedReferralCode(this.referralCodeInput ?? '');
    if (!code) return;
    this.referralCodeSubmitting = true;
    this.referralCodeError = undefined;
    this.cdr.markForCheck();
    try {
      const result = await this.referralService.claimAsync(code);
      if (this.viewDestroyed) return;
      if (!result.claimed) {
        this.referralCodeError = this.refusalMessage(result.refusal);
        return;
      }
      // the same event the hub emits for this action, so a claim made here is not
      // invisible in the funnel the programme is judged by.
      this.telemetry.trackEvent('referral_signup', { referral_source: 'typed_code' });

      // claimed — the answer to "what checkout does this person get?" has changed.
      await this.refetchAndReopenCheckoutAsync();
    } catch (err) {
      // reportCallFailure rather than trackAbnormalException: being offline is by
      // far the commonest way this call fails, and it is not a problem. A
      // problem-tier report marks the whole session as having gone wrong, which
      // suppresses the app-rating prompt for the rest of it.
      this.referralService.reportCallFailure('referral-claim-failed', err);
      this.referralCodeError = LL.referral.subscribe.codeEntry.failed();
    } finally {
      this.referralCodeSubmitting = false;
      // zoneless: a correctly-updated model still draws the old view without this.
      this.cdr.markForCheck();
    }
  }

  /**
   * Ask the server for the checkout again and re-open it.
   *
   * Used when something the checkout was built from has changed: a referral claim
   * (#4962), or the buyer's Advertising answer (#5587).
   */
  private async refetchAndReopenCheckoutAsync(): Promise<void> {
    this.payProPriceFetcher.resetForRefetch();
    await this.fetchPrices();
    if (this.viewDestroyed) return;

    // Re-open the checkout for the newly-resolved rail and prices.
    //
    // `fetchPrices` alone is NOT enough, and the reason is easy to miss. The
    // checkout is opened by `handleSelect`, which the normal load path calls once
    // via `attemptPriceLoadAsync`. By the time anybody types a code `selectedPlan`
    // is therefore already 0 — and `cs-radio-group.select()` only emits
    // `selectedChange` when the value actually CHANGES, so re-rendering the radio
    // group re-triggers nothing. Calling `handleSelect` directly is what gets past
    // that: it is a plain method, and only the template binding goes through the
    // radio group's emit-on-change rule.
    //
    // Left out, a claim on the PayPro rail switches `webProvider` to 'paddle' and
    // renders the inline frame while the rail's `selectPlan` never runs, so the
    // buyer stares at an empty checkout.
    //
    // CLOSE an already-open Paddle frame first, and this is the part that is easy
    // to get half-right. On the Paddle rail the frame is already open, so
    // `selectPlan` would take its `updateItems` path — which swaps the PRICE
    // and nothing else. `custom_data` is stamped once, at open time, and it carries
    // the `checkout_id` the server keyed this friend's offer record on — and the
    // tracking data holding the Advertising answer. The re-fetch above mints a NEW
    // checkout id, so leaving the frame open would convert the referral price
    // under the OLD id: the webhook would find no offer for it, see a referral
    // price was used, and report the friend's own perfectly legitimate purchase as
    // abuse — the exact false positive this design goes to some trouble to avoid.
    // Closing forces a fresh open that re-stamps both.
    if (this.webProvider === 'paddle') {
      this.paddleRail.closeBeforeReopen();
    }
    if (this.planOptions.length > 0) this.handleSelect(0);
  }

  /** Whether the server saw the Global Privacy Control header when it built this
   *  page's checkout config (#5663). Only the Paddle rail carries that answer
   *  back; on every other rail the browser's own reading is all there is. */
  private serverSawGpc(): boolean {
    return this.payProPriceFetcher.paddleConfig?.gpcDetected === true;
  }

  /**
   * Rebuild the checkout when this browser's Advertising answer changed after the
   * checkout was built (#5587).
   *
   * The checkout data carries the answer, and on the Paddle rail it is stamped
   * once, when the frame opens. A first-time visitor usually answers the cookie
   * dialog AFTER that, on top of this page — so without this, their answer would
   * never reach the server and an EU "yes" would never be reported. When to
   * rebuild, and when not to, is decided by decideAdConsentRefresh.
   */

  private async refreshCheckoutForAdConsentAsync(): Promise<void> {
    const decision = decideAdConsentRefresh({
      isNativePlatform: this.isNativePlatform,
      pageGone: this.viewDestroyed || this.exitingPage,
      loadingPrices: this.loadingPrices,
      purchaseComplete: this.paddleRail.purchaseComplete,
      hasPlans: this.planOptions.length > 0,
      builtWith: this.checkoutAdConsent,
      current: resolveEffectiveAdConsent(this.serverSawGpc()),
    });
    if (decision === AdConsentRefreshDecision.RetryAfterLoad) {
      this.safeTimeout(() => { void this.refreshCheckoutForAdConsentAsync(); }, 1000);
      return;
    }
    if (decision !== AdConsentRefreshDecision.Refresh) return;
    try {
      await this.refetchAndReopenCheckoutAsync();
    } catch (err) {
      // the checkout already on screen still works — it just carries the older
      // answer — so this is worth knowing about but not worth interrupting for
      this.logging.trackSilentProblem('CheckoutAdConsentRefreshFailed', { error: describeError(err) });
    } finally {
      this.cdr.markForCheck();
    }
  }

  /**
   * The wording for one refusal reason.
   *
   * Reuses the hub's exhaustive map rather than restating it — see
   * `REFERRAL_REFUSAL_MESSAGES`. A second copy would keep compiling with a stale set
   * of arms, which is the drift the exhaustiveness exists to prevent.
   */
  private refusalMessage(refusal: ReferralClaimRefusal | undefined): TranslatedString {
    return refusal ? REFERRAL_REFUSAL_MESSAGES[refusal]() : LL.referral.subscribe.codeEntry.failed();
  }


  // fill telemetry event params for remove_from_cart and add_to_cart events
  eventParamsForCartItem(plan: PlanOption) {
    return {
      currency: plan.currency,
      value: plan.price,
      items: [
        {
          item_id: plan.id,
          item_name: plan.name,
          item_category: 'subscription',
          price: plan.price,
        }
      ]
    };
  }

  handleSelect(i: number) {
    // track previous selection for telemetry
    let firstSelection = this.selectedPlan === undefined || this.selectedPlan === i;
    // if there was a different previous selection
    if (!firstSelection) {
      // remove the old one from the cart
      this.telemetry.trackEvent('remove_from_cart', this.eventParamsForCartItem(this.planOptions[this.selectedPlan]));
    }

    this.selectedPlan = i;
    let opt = this.planOptions[i];
    if (this.webProvider === 'paddle') {
      // open the inline checkout (first selection) or swap the plan on the
      // already-open frame (a radio switch). The rail manages the spinner.
      this.paddleRail.selectPlan(opt);
    } else {
      // point the checkout iframe at this plan's buy URL. The rail manages the
      // spinner, which its own frame-loaded message clears.
      this.payProRail.selectPlan(opt);
    }
    this.cdr.markForCheck();

    // add the new one to the cart (if this is the first time, then track this as begin checkout)
    this.telemetry.trackEvent(firstSelection ? 'begin_checkout' : 'add_to_cart', this.eventParamsForCartItem(opt));
  }

  /**
   * The buyer tapped Continue on Paddle's success screen. The rail verifies the
   * grant landed and takes the buyer off this page; see SubscribePaddleRail.
   */
  async onContinueAfterPaddlePurchase(): Promise<void> {
    await this.paddleRail.onContinueAsync();
  }

  // shared email-conflict verdict for both checkout rails. Logged-in: the typed
  // email must match the signed-in account ('ok' / 'mismatch'). Purchase-first:
  // the email must not already have an account ('ok' / 'exists'). 'error' means
  // the existence check itself failed.
  private async classifyCheckoutEmail(email: string): Promise<CheckoutEmailVerdict> {
    if (this.auth.user) {
      return email.toLowerCase() === this.auth.user.email?.toLowerCase() ? 'ok' : 'mismatch';
    }
    try {
      const userExistsResult = await this.cloudFunctions.checkUserExistsAsync({ email });
      return userExistsResult.exists ? 'exists' : 'ok';
    } catch (error) {
      this.logging.trackSilentProblem(error, 'Error checking email registration during checkout');
      return 'error';
    }
  }

  handleRedeem() {
    void this.navCtrl.pushAsync(IRedeemCodePage, {});
  }

  handleSignIn() {
    void this.navCtrl.setRootAsync(ILoginPage, { mode: 'login' });
  }

  handleSendSignInLink() {
    // ask straight away for the address they paid with and email a sign-in link
    // to it. this used to open the mail app first and wait 5 seconds, which told
    // a customer on the wrong account nothing about what to do next (#5894).
    void (async () => {
      const result = await this.alertCtrl.showAlertAsync<{ email: string }>({
        title: LL.subscribe.alerts.checkEmail.sendLinkTitle(),
        message: LL.subscribe.alerts.checkEmail.message(),
        inputs: [
          { type: 'email', name: 'email', placeholder: LL.auth.enterYourEmail() },
        ],
        buttons: [
          { text: LL.subscribe.alerts.checkEmail.sendLink(), role: AlertButtonRole.Primary, result: (data: any) => data },
        ],
        implicitCancelResult: null,
      });
      if (this.viewDestroyed || !result?.email) return;

      // decide what happened to the send first, then say so — a send that threw
      // used to skip the announcement and leave the user with nothing (see
      // resend-link-outcome.ts).
      const outcome = await resendSignInLinkAsync(
        () => this.cloudFunctions.signInByLink({ email: result.email, newUser: false, baseUrl: getSharedUrlOrigin(), uiLanguage: BOOT_LOCALE }),
        err => this.logging.trackSilentProblem('SubscribeResendSignInLinkFailed', { error: describeError(err) }),
      );

      // this block runs un-awaited, so an error while showing the message would
      // otherwise become an unhandled rejection nobody sees.
      try {
        await this.showResendOutcomeAsync(outcome);
      } catch (err) {
        this.logging.trackSilentProblem('SubscribeResendOutcomeAlertFailed', { outcome, error: describeError(err) });
      }
    })();
  }

  /** Shows the alert that matches what happened to a "Resend Link" request. */
  private async showResendOutcomeAsync(outcome: LinkResendOutcome): Promise<void> {
    const okButton = { text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: null };
    switch (outcome) {
      case LinkResendOutcome.AccountAmbiguous:
        // several accounts on this address means the server sent NO link, because
        // it cannot tell which account is theirs (#4505). Never claim an email
        // was sent when none was — say what happened and point them at support.
        await this.alertCtrl.showAlertAsync({
          message: LL.auth.errors.otlAmbiguousAccount({ email: getSupportEmail() }),
          buttons: [okButton],
        });
        return;
      case LinkResendOutcome.SendFailed:
        await this.alertCtrl.showAlertAsync({
          title: LL.subscribe.alerts.checkEmail.resendFailedTitle(),
          message: LL.subscribe.alerts.checkEmail.resendFailedMessage(),
          buttons: [okButton],
        });
        return;
      case LinkResendOutcome.Sent:
        await this.alertCtrl.showAlertAsync({
          title: LL.auth.emailLinkSent(),
          message: LL.auth.emailLinkSentMessage(),
          buttons: [okButton],
        });
        return;
    }
  }

  /**
   * Apple 3.1.2(c) per-plan trial disclosure line, or '' to hide it. The rail
   * assembles it and reports an unrenderable product; see SubscribeNativeRail.
   */
  trialDisclosureFor(product: IAPProduct): string {
    return this.nativeRail.trialDisclosureFor(product);
  }
}
